import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import EntryPage from 'pages/EntryPage'
import * as ApiSelectors from 'redux/selectors/api'
import FetchTypes from 'util/fetch-types'
import {
  attemptLogin,
  lookupMemberRecord,
  forgotPassword,
  verifyAccessCode,
  updatePassword,
  generateMfa,
  verifyMfa,
  fetchAuthChallenge,
} from 'redux/thunks/api'
import { fetchMe } from 'redux/thunks/members'
import queryString from 'query-string'
import Loading from '@ui-components-3/ui/lib/components/Loading'
import TestIds from 'util/TestIds'
import { useLocation } from 'react-router'

const EntryContainer = (props) => {
  const dispatch = useDispatch()

  const location = useLocation()

  const query = useMemo(() => {
    if (!location?.search) return {}
    return queryString.parse(location.search)
  }, [location])

  const handleLogin = async (email: string, password: string) => await dispatch(attemptLogin(email, password))
  const handleFetchAuthChallenge = async (email: string) => await dispatch(fetchAuthChallenge(email))
  // TODO: Replace types once api thunk is converted to typescript
  const handleMemberLookup = async ({ email, dob }: { email: string; dob?: string }) =>
    await dispatch(lookupMemberRecord({ email, dob }))
  const handleForgotPassword = async (email: string) => await dispatch(forgotPassword(email, 'member'))
  const handleVerifyAccessCode = async (code: string) => await dispatch(verifyAccessCode(code))
  const handleUpdatePassword = async (oldPassword: string, password: string) =>
    await dispatch(updatePassword(oldPassword, password))
  const handleFetchMe = async () => await dispatch(fetchMe())
  const handleGenerateMfa = async (mfaChoice: { deliveryMethod: string; phoneId?: string }) =>
    await dispatch(generateMfa(mfaChoice))
  const handleVerifyMfa = async (mfaCode: string) => await dispatch(verifyMfa(mfaCode))

  const isLoggingIn = useSelector(ApiSelectors.isTypeFetching(FetchTypes.login))
  const isFetchingAuthChallenge = useSelector(ApiSelectors.isTypeFetching(FetchTypes.authChallenge))
  const isLookingUpMember = useSelector(ApiSelectors.isTypeFetching(FetchTypes.lookupMember))
  const isFetchingMe = useSelector(ApiSelectors.isTypeFetching(FetchTypes.getMe))
  const isUpdatingPassword = useSelector(ApiSelectors.isTypeFetching(FetchTypes.updatePassword))
  const isClientLoading = useSelector(ApiSelectors.isTypeFetching(FetchTypes.getClientByDomain))
  const isGeneratingMfa = useSelector(ApiSelectors.isTypeFetching(FetchTypes.generateMfa))
  const isVerifyingMfa = useSelector(ApiSelectors.isTypeFetching(FetchTypes.verifyMfa))

  const mfaOptions = useSelector(ApiSelectors.getMfaOptions)

  const isApiFetching =
    isLoggingIn ||
    isFetchingAuthChallenge ||
    isLookingUpMember ||
    isFetchingMe ||
    isUpdatingPassword ||
    isClientLoading ||
    isGeneratingMfa ||
    isVerifyingMfa

  return (
    <>
      <EntryPage
        {...props}
        query={query}
        attemptLogin={handleLogin}
        fetchMe={handleFetchMe}
        updatePassword={handleUpdatePassword}
        forgotPassword={handleForgotPassword}
        lookupMemberRecord={handleMemberLookup}
        verifyAccessCode={handleVerifyAccessCode}
        fetchAuthChallenge={handleFetchAuthChallenge}
        generateMfa={handleGenerateMfa}
        verifyMfa={handleVerifyMfa}
        mfaOptions={mfaOptions}
      />
      <Loading shown={isApiFetching} variant="big" data-testid={TestIds.login.view.loading} />
    </>
  )
}

export default EntryContainer
